import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BsModalService } from "ngx-bootstrap/modal";
import { Subscription, debounceTime, tap } from "rxjs";
import { CommonApiService } from "src/app/services/common-api-service";
import { EventService } from "src/app/services/event.service";
import { GameAPIService } from "src/app/services/game-api.service";
import { PopupService } from "src/app/services/popup.service";
import { WithdrawNftComponent } from "src/app/shared/components/withdraw-nft/withdraw-nft.component";
import { API_Const } from "src/app/shared/constants/apiRouteConst";
import {
  ACTIONS,
  LootBoxesTabsEnum,
  PRIZE_STATUS,
  SORT_ENUM,
  SPIN_WIN_PRIZES,
  verifyLanguage,
  CURRENCY,
  BLOCKCHAINS,
  STORAGE_KEYS,
  IDENTIFIERS,
} from "src/app/shared/constants/app-enums";
import { BH_LOGIN_URL, MESSAGES, ROUTES, sessionSubject, STATIC_NFT } from "src/app/shared/constants/common.constants";
import { IPrizeCard } from "../lootbox.interface";
import { HttpErrorResponse } from "@angular/common/http";

@Component({
  selector: "app-lootbox-dashboard",
  templateUrl: "./lootbox-dashboard.component.html",
  styleUrls: ["./lootbox-dashboard.component.scss"],
})
export class LootboxDashboardComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  lootBoxesTabs = LootBoxesTabsEnum;
  activeTab: LootBoxesTabsEnum = LootBoxesTabsEnum.PLAY;
  LootBoxesTabsEnum = LootBoxesTabsEnum;
  SORT_ENUM = SORT_ENUM;
  isPlayTabActive = true;
  isDropdownOpen = false;
  filterConfig = {
    sortBy: SORT_ENUM.DESC,
    searchTxt: "",
  };
  lootboxList: IPrizeCard[] = [];
  lang = "en";
  myNfts: IPrizeCard[] = [];
  searchLootbox = new FormControl()
  previousValue = '';
  fetchingLootboxes = true;
  isUSDDefault = false;
  selectedNfts: IPrizeCard[] = [];
  selectedChain = '';
  gameSession: any;
  isDemo = false;
  loadingNFTs = true;

  constructor(
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private eventService: EventService,
    private router: Router,
    private apiService: CommonApiService,
    private popupService: PopupService,
    private modalService: BsModalService,
    private route: ActivatedRoute,
    private gameApiService: GameAPIService,
  ) {
  }

  initializeSubscriptions() {
    this.subscriptions.push(this.route.queryParamMap.subscribe(params => {
      this.isDemo = JSON.parse(params.get('is_demo') as string);
      this.gameApiService.setIsDemo = this.isDemo;
      const token = params.get('token');
      let storedToken = (localStorage.getItem(STORAGE_KEYS.SESSION) as string);
      if (storedToken && token?.slice(token.length - 5) !== storedToken?.slice(storedToken.length - 5)) {
        localStorage.clear();
        storedToken = '';
      }
      if (!this.isDemo && !(storedToken)?.length) {
        this.gameApiService.setGameToken = token;
      } else if (this.isDemo) {
        this.gameApiService.removeSession();
      }
      const currency = params.get('currency');
      this.gameApiService.userCurrency = (currency || CURRENCY.USD) as CURRENCY;
      this.isUSDDefault = this.gameApiService.userCurrency === CURRENCY.USD;
      const platform = params.get('platform');
      this.gameApiService.currentPlatform = platform as string;
    }));
    this.subscriptions.push(this.activatedRoute.params.subscribe((params) => {
      const lang = params["lang"];
      if (lang && verifyLanguage(lang)) {
        this.lang = lang;
      } else {
        const localLang = localStorage.getItem(STORAGE_KEYS.CURRENT_LANG);
        if (localLang && verifyLanguage(localLang)) {
          this.lang = localLang;
        } else {
          const userLang = navigator.language?.split("-")[0];
          this.lang = userLang && verifyLanguage(userLang) ? userLang : "en";
        }
      }
    }));
    this.translate.setDefaultLang("en");
    this.translate.use(this.lang);
    this.eventService.setLang(this.lang);
    this.isUSDDefault = this.gameApiService.userCurrency === CURRENCY.USD;
    const isFromMyNFTs = JSON.parse(localStorage.getItem(STORAGE_KEYS.FROM_MY_NFTS) as string);
    if (isFromMyNFTs) {
      this.activeTab = LootBoxesTabsEnum.MY_PRIZES;
      this.isPlayTabActive = false;
      localStorage.removeItem(STORAGE_KEYS.FROM_MY_NFTS)
    }
    this.subscriptions.push(sessionSubject.subscribe(session => {
      this.gameSession = session
    }));
    this.searchLootbox.valueChanges.pipe(tap((value) => {
      if (!value && this.previousValue) {
        this.fetchingLootboxes = true;
      }
    }), debounceTime(1000)).subscribe((value) => {
      if (!value && this.previousValue) {
        this.getLootboxes();
      } else if (value.length > 2) {
        this.getLootboxes(true);
      }
      this.previousValue = value;
    });
    this.getLootboxes();
  }

  ngOnInit(): void {
    this.initializeSubscriptions();
  }

  changeTab(event: any) {
    if (event.id === LootBoxesTabsEnum.MY_PRIZES) {
      // Check if isDemo or session token is not present
      if (this.isDemo || !this.gameApiService.sessionToken) {
        window.open(BH_LOGIN_URL, "_parent");
        location.reload();
        return;
      }
      this.isPlayTabActive = event.id === LootBoxesTabsEnum.PLAY;
      this.getMyNfts();
      return;
    }
    this.isPlayTabActive = event.id === LootBoxesTabsEnum.PLAY;
    this.getLootboxes();
  }

  onCardAction(actionData: { action: ACTIONS, data?: any }, gameCard: IPrizeCard) {
    if (actionData.action === ACTIONS.CLICK) {
      if (this.isPlayTabActive) {
        this.router.navigate([this.lang, ROUTES.LOOTBOX, gameCard.prizeId]);
        return;
      } else {
        // Setting this flag to check if navigation to NFT Details was from My NFTs or Lootbox Contents
        localStorage.setItem(STORAGE_KEYS.FROM_MY_NFTS, "true");
        this.router.navigate([this.lang, "nft", `${gameCard?.slug}`, gameCard?.id], {
          queryParams: {
            prizeId: gameCard.prizeId
          }
        });
        return;
      }
    } else if (actionData.action === ACTIONS.CHECK_BOX) {
      this.checkNFTs({ ...gameCard });
    }
  }

  checkNFTs(selectedNft: IPrizeCard) {
    this.myNfts.forEach((nft: IPrizeCard) => {
      if (nft.type !== SPIN_WIN_PRIZES.NFT) {
        return;
      }
      if (selectedNft.prizeId === nft.prizeId) {
        (nft.checkbox as any).isChecked = !nft.checkbox?.isChecked;
        if (nft.checkbox?.isChecked) {
          this.selectedChain = nft.chain as string;
          // Incase of same NFT occurs multiple times with different status
          if (!selectedNft.statusType) {
            this.selectedNfts.push(nft);
          }
        } else {
          this.selectedNfts = this.selectedNfts.filter((nft: IPrizeCard) => nft.prizeId !== selectedNft.prizeId && nft.type === SPIN_WIN_PRIZES.NFT);
          if (!this.selectedNfts.length) {
            this.selectedChain = '';
          }
        }
      }
    });
    if (this.selectedChain) {
      this.myNfts.forEach((nft: IPrizeCard) => {
        if (nft.statusType === PRIZE_STATUS.WITHDRAWN || nft.statusType === PRIZE_STATUS.CASH_SWAP || nft.type === SPIN_WIN_PRIZES.CUSTOM) {
          return;
        }
        nft.statusType = nft.chain === this.selectedChain ? undefined : PRIZE_STATUS.NON_COMPATIBLE;
        nft.checkbox!.hasCheckBox = (nft.statusType !== PRIZE_STATUS.NON_COMPATIBLE) as any;
      })
    } else {
      this.myNfts.forEach((nft: IPrizeCard) => {
        if (nft.statusType === PRIZE_STATUS.WITHDRAWN || nft.statusType === PRIZE_STATUS.CASH_SWAP || nft.type === SPIN_WIN_PRIZES.CUSTOM) {
          return;
        }
        nft.statusType = undefined;
        nft.checkbox!.hasCheckBox = true;
        nft.checkbox!.isChecked = false;
      })
    }
  }

  getLootboxes(isSearchMode?: boolean) {
    this.fetchingLootboxes = true;
    let queryParams = `?onlyActive=true&field=${this.isUSDDefault ? SORT_ENUM.PRICE_USD : SORT_ENUM.PRICE_EURO}`;
    if (this.filterConfig.sortBy) {
      queryParams += `&orderBy=${this.filterConfig.sortBy}`;
    }
    if (isSearchMode && this.searchLootbox.value) {
      queryParams += `&keyword=${this.searchLootbox.value}`
    }
    this.subscriptions.push(this.apiService
      .get(`${API_Const.lootboxes.games}${queryParams}`, !this.isDemo)
      .subscribe({
        next: (res) => {
          this.lootboxList = [];
          const {
            result,
          } = res;
          if (!result?.length) {
            this.fetchingLootboxes = false;
            return;
          }
          this.lootboxData = result;
          this.fetchingLootboxes = false;
        },
        error: (err: HttpErrorResponse) => {
          this.fetchingLootboxes = false;
          // Return when status is 0, interceptor handles this error
          if (!err.status) {
            return;
          }
          if (err?.error?.message) {
            this.popupService.messsageAlert("error", err?.error?.message);
            return;
          }
          this.popupService.messsageAlert("error", MESSAGES.SOMETHING_WENT_WRONG);
        },
      }));
  }

  sortLootboxes(sortType: SORT_ENUM) {
    this.filterConfig.sortBy = sortType;
    this.getLootboxes(!!this.searchLootbox);
  }

  restrictWhitespace(event: any) {
    let k = event.charCode;
    let value = event.target.value;
    if (
      (value.length == 0 && k == 32) ||
      (value.length > 1 && value[value.length - 1] == " " && k == 32)
    ) {
      return false;
    }
    return true;
  }

  set lootboxData(games: any[]) {
    this.lootboxList = [];
    games.forEach((game: any) => {
      this.lootboxList.push({
        title: game.topPrize.type === SPIN_WIN_PRIZES.NFT ? `#${game.topPrize?.metaData?.nftId}` : '',
        items: game.prizeCount,
        fees: this.isUSDDefault ? +(+game.fee_usd).toFixed(2) : +(+game.fee_euro).toFixed(2),
        value: this.isUSDDefault ? game.topPrize.amountInUsd : game.topPrize.amountInEuro,
        type: game.topPrize.type,
        desc: game.topPrize?.metaData?.collection,
        img: game.topPrize?.imageUrl,
        unit: this.isUSDDefault ? CURRENCY.USD : CURRENCY.EURO,
        prizeId: game.id,
        gameHeroImage: game.gameHeroImage ?? '',
        name: game.name ?? ''
      });
    });
  }

  onEnter(event: any): void {
  }

  getMyNfts() {
    this.myNfts = [];
    this.selectedChain = '';
    this.selectedNfts = [];
    this.loadingNFTs = true;
    this.subscriptions.push(this.apiService.get(API_Const.lootboxes.getUserPrizes, true).subscribe({
      next: (res) => {
        if (res.success) {
          const { result } = res;
          result.forEach((prize: any) => {
            const statusType = prize.prizeType === SPIN_WIN_PRIZES.NFT ? (prize?.status_of_nft === PRIZE_STATUS.KEPT ? '' : prize?.status_of_nft) : (prize?.custom_prize_status);
            this.myNfts.push({
              title: prize?.custom_prize_label ?? `#${prize.nft_id}`,
              type: prize.prizeType,
              value: this.isUSDDefault ? (prize?.nft_price_usd || prize?.custom_prize_usd) : (prize?.nft_price_euro || prize?.custom_prize_euro),
              chain: (prize.nft_chain as string)?.toUpperCase() as BLOCKCHAINS,
              desc: prize.nft_name ?? prize.custom_prize_desc,
              checkbox: {
                hasCheckBox: prize.prizeType === SPIN_WIN_PRIZES.NFT && prize.status_of_nft === PRIZE_STATUS.KEPT,
                isChecked: false
              },
              unit: this.isUSDDefault ? CURRENCY.USD : CURRENCY.EURO,
              statusType,
              img: prize.prizeType === SPIN_WIN_PRIZES.NFT ? prize.nft_image : prize.custom_prize_image,
              prizeId: prize.id,
              slug: prize.slug,
              id: prize?.nft_id,
            })
          });
          this.loadingNFTs = false;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.myNfts = [];
        this.loadingNFTs = false;
        // Return when status is 0, interceptor handles this error
        if (!err.status) {
          return;
        }
        if (err?.error?.message) {
          this.popupService.messsageAlert("error", err?.error?.message);
          return;
        }
        this.popupService.messsageAlert("error", MESSAGES.SOMETHING_WENT_WRONG);
      }
    }));
  }

  selectAllNfts() {
    this.myNfts.forEach((nft: IPrizeCard) => nft.checkbox ? nft.checkbox.isChecked = !nft.checkbox?.isChecked : false);
  }

  withdrawNFTs() {
    const selectedNFTs = this.selectedNfts.filter((nft: IPrizeCard) => !nft.statusType && nft.type === SPIN_WIN_PRIZES.NFT)
    const walletAddressDialog = this.modalService.show(WithdrawNftComponent, {
      id: IDENTIFIERS.WITHDRAW_NFT,
      class: "modal-dialog-centered spin-win-dialog",
      backdrop: "static",
      animated: false,
      ignoreBackdropClick: false,
      initialState: {
        selectedChain: this.selectedChain,
        selectedNFTs
      },
    });
    walletAddressDialog.onHidden?.subscribe(() => {
      if (walletAddressDialog.content?.withdrawalStatus) {
        this.getMyNfts();
      }
    })
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }
}
